import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, X, Smartphone } from 'lucide-react';

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const ios = /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase());
    setIsIOS(ios);

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after 3 seconds if not dismissed before
      setTimeout(() => {
        if (!localStorage.getItem('pwa-prompt-dismissed')) {
          setShowPrompt(true);
        }
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show iOS instructions if on iOS and not installed
    if (ios && !standalone && !localStorage.getItem('pwa-prompt-dismissed')) {
      setTimeout(() => setShowPrompt(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('PWA installed');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  if (isStandalone || !showPrompt) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 bg-white rounded-lg shadow-2xl border border-slate-200 p-4 z-50 animate-in slide-in-from-bottom-5">
      <button 
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
      >
        <X className="w-5 h-5" />
      </button>
      
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
          <span className="text-white text-xl font-bold">E</span>
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-slate-900 text-sm">Install Elmwood Management</h3>
          <p className="text-xs text-slate-500 mt-1">
            {isIOS 
              ? "Tap the share button and select 'Add to Home Screen'" 
              : "Install for quick access from your home screen"
            }
          </p>
        </div>
      </div>

      {!isIOS && deferredPrompt && (
        <Button 
          onClick={handleInstall}
          className="w-full mt-3 bg-orange-500 hover:bg-orange-600 text-white rounded-sm"
          size="sm"
        >
          <Download className="w-4 h-4 mr-2" />
          Install App
        </Button>
      )}

      {isIOS && (
        <div className="mt-3 p-3 bg-slate-50 rounded-lg">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <Smartphone className="w-4 h-4" />
            <span>Tap <strong>Share</strong> → <strong>Add to Home Screen</strong></span>
          </div>
        </div>
      )}
    </div>
  );
}
